export const FLOW_LIMITS = { maxSteps: 50, maxText: 1000, maxOptions: 8 };
export function normalizeFlowInput(value) {
    return value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
}
function nextOf(step) {
    if (step.type === "message")
        return step.next ? [step.next] : [];
    if (step.type === "question")
        return [...step.options.map((o) => o.next), ...(step.fallbackNext ? [step.fallbackNext] : [])];
    return [];
}
/** Lista de problemas da definição (vazia = válida). Mensagens em português para a tela. */
export function validateFlow(flow) {
    const errors = [];
    if (!normalizeFlowInput(flow.triggerKeyword ?? ""))
        errors.push("Informe a palavra que inicia o fluxo.");
    if (!Array.isArray(flow.steps) || flow.steps.length === 0)
        return [...errors, "O fluxo precisa de ao menos um passo."];
    if (flow.steps.length > FLOW_LIMITS.maxSteps)
        errors.push(`No máximo ${FLOW_LIMITS.maxSteps} passos.`);
    const ids = new Set();
    for (const step of flow.steps) {
        if (!step.id?.trim()) {
            errors.push("Todo passo precisa de um identificador.");
            continue;
        }
        if (ids.has(step.id))
            errors.push(`Identificador repetido: ${step.id}.`);
        ids.add(step.id);
        const text = "text" in step ? step.text : "";
        if (step.type !== "end" && !text?.trim())
            errors.push(`O passo ${step.id} precisa de texto.`);
        if (text && text.length > FLOW_LIMITS.maxText)
            errors.push(`O texto do passo ${step.id} passa de ${FLOW_LIMITS.maxText} caracteres.`);
        if (step.type === "question") {
            if (step.options.length === 0)
                errors.push(`A pergunta ${step.id} precisa de ao menos uma opção.`);
            if (step.options.length > FLOW_LIMITS.maxOptions)
                errors.push(`A pergunta ${step.id} tem opções demais.`);
            for (const option of step.options)
                if (!option.label.trim() || !normalizeFlowInput(option.keyword))
                    errors.push(`Toda opção da pergunta ${step.id} precisa de rótulo e palavra.`);
        }
    }
    if (!ids.has(flow.startStepId))
        errors.push("O passo inicial não existe.");
    for (const step of flow.steps)
        for (const target of nextOf(step))
            if (!ids.has(target))
                errors.push(`O passo ${step.id} aponta para um passo inexistente (${target}).`);
    // Ciclo só de mensagens = a conversa nunca esperaria o cliente.
    const byId = new Map(flow.steps.map((s) => [s.id, s]));
    for (const start of flow.steps.filter((s) => s.type === "message")) {
        const seen = new Set();
        let cursor = start;
        while (cursor && cursor.type === "message" && cursor.next) {
            if (seen.has(cursor.id)) {
                errors.push(`Ciclo de mensagens sem pergunta passando por ${cursor.id}.`);
                break;
            }
            seen.add(cursor.id);
            cursor = byId.get(cursor.next);
        }
    }
    return [...new Set(errors)];
}
/** Texto da pergunta com as opções numeradas, como o cliente a veria. */
export function renderQuestion(step) {
    return [step.text, ...step.options.map((option, index) => `${index + 1}. ${option.label}`)].join("\n");
}
function runFrom(flow, stepId, transcript) {
    const byId = new Map(flow.steps.map((s) => [s.id, s]));
    let cursor = stepId;
    for (let guard = 0; cursor && guard <= FLOW_LIMITS.maxSteps * 2; guard += 1) {
        const step = byId.get(cursor);
        if (!step)
            break;
        if (step.type === "message") {
            transcript.push({ from: "bot", text: step.text, stepId: step.id });
            cursor = step.next;
            continue;
        }
        if (step.type === "question") {
            transcript.push({ from: "bot", text: renderQuestion(step), stepId: step.id });
            return { status: "waiting", waitingStepId: step.id, transcript };
        }
        if (step.type === "handoff") {
            transcript.push({ from: "bot", text: step.text, stepId: step.id });
            return { status: "handoff", waitingStepId: null, transcript };
        }
        if (step.text)
            transcript.push({ from: "bot", text: step.text, stepId: step.id });
        return { status: "ended", waitingStepId: null, transcript };
    }
    return { status: "ended", waitingStepId: null, transcript };
}
/** Inicia o fluxo no passo inicial. Lança se a definição for inválida. */
export function startFlow(flow) {
    const errors = validateFlow(flow);
    if (errors.length)
        throw new Error(`invalid_flow:${errors[0]}`);
    return runFrom(flow, flow.startStepId, []);
}
/** Aplica a resposta do cliente ao passo em espera. Resposta não reconhecida repete a pergunta (ou segue o `fallbackNext`). */
export function answerFlow(flow, run, input) {
    if (run.status !== "waiting" || !run.waitingStepId)
        return run;
    const step = flow.steps.find((s) => s.id === run.waitingStepId);
    if (!step || step.type !== "question")
        return run;
    const transcript = [...run.transcript, { from: "customer", text: input }];
    const typed = normalizeFlowInput(input);
    const byNumber = /^\d+$/.test(typed) ? step.options[Number(typed) - 1] : undefined;
    const chosen = byNumber ?? step.options.find((o) => normalizeFlowInput(o.keyword) === typed || normalizeFlowInput(o.label) === typed);
    if (chosen)
        return runFrom(flow, chosen.next, transcript);
    if (step.fallbackNext)
        return runFrom(flow, step.fallbackNext, transcript);
    transcript.push({ from: "bot", text: "Não entendi. Responda com o número ou a palavra de uma das opções.", stepId: step.id });
    transcript.push({ from: "bot", text: renderQuestion(step), stepId: step.id });
    return { status: "waiting", waitingStepId: step.id, transcript };
}
/** Simula o fluxo inteiro com uma lista de respostas do cliente (sem enviar nada). */
export function simulateFlow(flow, replies) {
    return replies.reduce((run, reply) => answerFlow(flow, run, reply), startFlow(flow));
}
/** O fluxo dispara quando a mensagem contém a palavra-gatilho (sem acento, qualquer posição). */
export function flowTriggered(flow, message) {
    const keyword = normalizeFlowInput(flow.triggerKeyword);
    return Boolean(keyword) && new RegExp(`(^|[^a-z0-9])${keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z0-9]|$)`).test(normalizeFlowInput(message));
}
